import { CdkScrollable } from '@angular/cdk/scrolling';
import { Component, HostListener, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { NgIf } from '@angular/common';

import { TranslationService } from '../../../../../../shared/services/translation.service';
import { pageTransitions } from '../../../../../../shared/animations/page-transitions';
import {
	StepClickedEvent,
	StepStatus,
	StepperComponent
} from '../../../../../../shared/components/stepper/stepper.component';
import { LocationService } from '../../../../../../shared/services/location.service';
import { AddSubOrganizationService } from '../../../../../services/add-suborg.service';
import {
	AddSubOrgPageContent
} from '../../../../../../shared/models/add-suborg-info';
import { OrganizationDetails } from '../../../../../models/organization';
import { UserService } from '../../../../../../shared/services/user.service';
import { User } from '../../../../../../shared/models/user';
import { AfterViewInitDirective } from '../../../../../../shared/directives/after-view-init.directive';
import { LoadingSpinnerComponent } from '../../../../../../shared/components/utils/loading-spinner.component';
import { PageHeaderComponent } from '../../../../../../shared/components/page-header/page-header.component';
import { ScrollerDirective } from '../../../../../../shared/directives/scroller.directive';

@Component({
	selector: 'copo-add-sub-organization',
	templateUrl: './add-sub-organization.component.html',
	styleUrl: './add-sub-organization.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		ScrollerDirective,
		PageHeaderComponent,
		NgIf,

		MatButtonModule,
		TranslateModule,
		StepperComponent,
		LoadingSpinnerComponent,
		RouterOutlet,
		AfterViewInitDirective,
	],
})
export class AddSubOrganizationComponent implements OnDestroy {
	@ViewChild('scroller', { read: CdkScrollable }) scrollBar?: CdkScrollable;
	pageContent: AddSubOrgPageContent | undefined;
	isLoading = false;
	hideNextBtn = false;
	pageTitle: string;
	organizationName = '';
	vatNumber = '';
	currentSelectedOrg?: OrganizationDetails;
	administrator = false;
	email = '';
	sub?: Subscription;
	orgId = '';
	userId = '';
	isAnyOneOrgSelected = false;
	isAnyOneUserSelected = false;
	isNewSubOrg = false;
	vatNumberExistsCheck = false;
	user?: User;
	readonly steps;

	private readonly pageTitleTranslationCode = 'add-suborg.add-suborg';
	private readonly routeSub: Subscription = Subscription.EMPTY;

	constructor(
		private readonly translationService: TranslationService,
		private readonly router: Router,
		private readonly route: ActivatedRoute,
		private readonly addSubOrgService: AddSubOrganizationService,
		private readonly locationService: LocationService,

		private readonly snackBar: MatSnackBar,
		private readonly userService: UserService
	) {
		this.steps = [
			{
				label: this.translationService.getTR('add-suborg.organization-details'),
				status: StepStatus.CURRENT,
			},
			{
				label: this.translationService.getTR('add-suborg.matching-organizations'),
				status: StepStatus.DISABLED,
			},
			{
				label: this.translationService.getTR('add-suborg.administrator'),
				status: StepStatus.DISABLED,
			},
			{
				label: this.translationService.getTR('add-suborg.confirmation'),
				status: StepStatus.DISABLED,
			}
		];
		this.user = this.userService.user;
		if (!this.route.parent?.parent?.parent) {
			throw new Error('wrong url');
		}
		this.routeSub = this.route.parent.paramMap.subscribe(params => {
			const idString = params.get('id');
			if (idString === null) {
				return;
			}
			this.orgId = idString;
			this.addSubOrgService.updateOrgId(this.orgId);
		});
		this.pageTitle = this.translationService.getTR(this.pageTitleTranslationCode);
		this.sub = this.addSubOrgService.isNewSubOrg.subscribe(isNewSubOrg => {
			this.isNewSubOrg = isNewSubOrg;
		});

		this.sub = this.addSubOrgService.userId.subscribe(userID => {
			this.userId = userID;
		});

		this.sub = this.addSubOrgService.organizationName.subscribe(organizationName => {
			this.organizationName = organizationName;
		});

		this.sub = this.addSubOrgService.vatNumber.subscribe(vatNumber => {
			this.vatNumber = vatNumber;
		});

		this.sub = this.addSubOrgService.isAnyOneOrgSelected.subscribe(isAnyOneOrgSelected => {
			this.isAnyOneOrgSelected = isAnyOneOrgSelected;
		});

		this.sub = this.addSubOrgService.isAnyOneUserSelected.subscribe(isAnyOneUserSelected => {
			this.isAnyOneUserSelected = isAnyOneUserSelected;
		});

		this.sub = this.addSubOrgService.vatNumberExistsCheck.subscribe(vatNumberExistsCheck => {
			this.vatNumberExistsCheck = vatNumberExistsCheck;
		});

		this.clearAll();
	}

	@HostListener('window:beforeunload') goToPage() {
		if (this.addSubOrgService.getCurrentRoute().endsWith('/details/addSubOrg/stepConfirmation')) {
			this.locationService.go(-3, `/user/organization/${ this.orgId }/details/addSubOrg/stepOrganizationDetails`);
		} else if (this.addSubOrgService.getCurrentRoute().endsWith('/details/addSubOrg/stepChooseAdministrator')) {
			this.locationService.go(-2, `/user/organization/${ this.orgId }/details/addSubOrg/stepOrganizationDetails`);
		} else if (this.addSubOrgService.getCurrentRoute().endsWith('/details/addSubOrg/stepMatchingOrganizations')) {
			this.locationService.go(-1, `/user/organization/${ this.orgId }/details/addSubOrg/stepOrganizationDetails`);
		}
		this.clearAll();
	}

	goToOrganizationDetails() {
		this.clearAll();
		this.locationService.go(-1, `/user/organization/${ this.orgId }/details`);
	}

	goBack() {
		this.locationService.go(-1, `/user/organization/${ this.orgId }/details`);
		this.clearAll();
	}

	clearAll() {
		this.addSubOrgService.updateOrganizationName('');
		this.addSubOrgService.updateVatNumber('');
		this.addSubOrgService.updateIsAnyOneOrgChoiceSelected(false);
		this.addSubOrgService.updateNodeSelectedMatchingOrgs(undefined);
		this.addSubOrgService.updateNodeSelectedUsers(undefined);
		this.addSubOrgService.setSelectedUser(undefined);
		this.addSubOrgService.updateEmail('');
		this.addSubOrgService.updateFirstName('');
		this.addSubOrgService.updateLastName('');
		this.addSubOrgService.updateVatNumberExists(false);
		this.addSubOrgService.setSelectedOrg(undefined);
	}

	async onStepClicked(event: StepClickedEvent) {
		this.steps[event.stepIndex].status = StepStatus.CURRENT;
		switch (event.stepIndex) {
		case 3:
			this.steps[event.stepIndex - 1].status = StepStatus.ENABLED;
			this.steps[event.stepIndex - 2].status = StepStatus.ENABLED;
			this.steps[event.stepIndex - 3].status = StepStatus.ENABLED;
			await this.router.navigate(
				[`/user/organization/${ this.orgId }/details/addSubOrg/stepConfirmation`],
				{ relativeTo: this.route }
			);
			this.hideNextBtn = true;
			break;
		case 2:
			this.steps[event.stepIndex - 1].status = StepStatus.ENABLED;
			this.steps[event.stepIndex - 2].status = StepStatus.ENABLED;
			this.steps[event.stepIndex + 1].status = StepStatus.DISABLED;
			await this.router.navigate(
				[`/user/organization/${ this.orgId }/details/addSubOrg/stepChooseAdministrator`],
				{ relativeTo: this.route }
			);
			this.hideNextBtn = false;
			break;
		case 1:
			this.steps[event.stepIndex + 1].status = StepStatus.DISABLED;
			this.steps[event.stepIndex - 1].status = StepStatus.ENABLED;
			this.steps[event.stepIndex + 2].status = StepStatus.DISABLED;
			await this.router.navigate(
				[`/user/organization/${ this.orgId }/details/addSubOrg/stepMatchingOrganizations`],
				{ relativeTo: this.route }
			);
			this.hideNextBtn = false;
			break;
		default:
			this.steps[event.stepIndex + 1].status = StepStatus.DISABLED;
			this.steps[event.stepIndex + 2].status = StepStatus.DISABLED;
			this.steps[event.stepIndex + 3].status = StepStatus.DISABLED;
			await this.router.navigate(
				[`/user/organization/${ this.orgId }/details/addSubOrg/stepOrganizationDetails`],
				{ relativeTo: this.route }
			);
			this.hideNextBtn = false;
			break;
		}
	}

	onActivate(pageContent: AddSubOrgPageContent) {
		this.pageContent = pageContent;
	}

	async goToNext() {
		if (this.steps.findIndex(step => step.status === StepStatus.CURRENT) === 0) {
			this.pageContent?.checkErrors();
			if (this.organizationName && this.vatNumber && this.pageContent?.checkErrors()) {
				this.steps[1].status = StepStatus.CURRENT;
				this.steps[2].status = StepStatus.DISABLED;
				this.steps[3].status = StepStatus.DISABLED;
				this.steps[0].status = StepStatus.ENABLED;
				await this.router.navigate(
					[`/user/organization/${ this.orgId }/details/addSubOrg/stepMatchingOrganizations`],
					{ relativeTo: this.route }
				);
			}
		} else if (this.steps.findIndex(step => step.status === StepStatus.CURRENT) === 1
		) {
			this.pageContent?.checkErrors();
			if (this.pageContent?.checkErrors() && ((this.isAnyOneOrgSelected && !this.isNewSubOrg)
				|| (this.isNewSubOrg && !this.vatNumberExistsCheck))) {
				this.steps[1].status = StepStatus.ENABLED;
				this.steps[2].status = StepStatus.CURRENT;
				this.steps[0].status = StepStatus.ENABLED;
				this.steps[3].status = StepStatus.DISABLED;
				await this.router.navigate(
					[`/user/organization/${ this.orgId }/details/addSubOrg/stepChooseAdministrator`],
					{ relativeTo: this.route }
				);
			}
		} else if (this.steps.findIndex(step => step.status === StepStatus.CURRENT) === 2
		) {
			this.pageContent?.checkErrors();
			if (this.pageContent?.checkErrors() && ((this.isNewSubOrg && this.isAnyOneUserSelected)
				|| !this.isNewSubOrg)) {
				this.steps[1].status = StepStatus.ENABLED;
				this.steps[2].status = StepStatus.ENABLED;
				this.steps[0].status = StepStatus.ENABLED;
				this.steps[3].status = StepStatus.CURRENT;
				await this.router.navigate(
					[`/user/organization/${ this.orgId }/details/addSubOrg/stepConfirmation`],
					{ relativeTo: this.route }
				);
				this.hideNextBtn = true;
			}
		}
	}

	isButtonSubmitVisible() {
		return !this.isLoading && this.steps.findIndex(step => step.status === StepStatus.CURRENT) === 3;
	}

	addSubOrg() {
		if (this.isNewSubOrg) {
			this.createNewSubOrg();
		} else {
			this.addExistingOrgAsSubOrg();
		}
	}

	createNewSubOrg() {
		const requestBody: OrganizationDetails = {
			name: this.organizationName,
			vatNumber: this.vatNumber,
			administratorId: Number(this.userId)
		};
		this.addSubOrgService.addNewSubOrg(Number(this.orgId), requestBody).then(async () => {
			await this.userService.updateUserData();
			this.goToOrganizationDetails();
		}).catch((error: unknown) => {
			if (error instanceof HttpErrorResponse && this.user && !this.user.readOnly) {
				this.displayErrorMessage((<{ message: string }>error.error).message);
			}
		});
	}

	addExistingOrgAsSubOrg() {
		this.sub = this.addSubOrgService.currentSelectedOrg.subscribe(selectedOrg => {
			this.currentSelectedOrg = selectedOrg;
		});
		if (!this.currentSelectedOrg) {
			return;
		}
		const requestBody: OrganizationDetails = {
			name: this.currentSelectedOrg.name,
			vatNumber: this.currentSelectedOrg.vatNumber,
		};
		this.addSubOrgService.addSubOrg(Number(this.orgId), requestBody).then(() => {
			this.goToOrganizationDetails();
		}).catch((error: unknown) => {
			if (error instanceof HttpErrorResponse && this.user && !this.user.readOnly) {
				this.displayErrorMessage((<{ message: string }>error.error).message);
			}
		});
	}

	ngOnDestroy() {
		this.routeSub.unsubscribe();
		if (this.sub !== undefined) {
			this.sub.unsubscribe();
		}
	}

	displayErrorMessage(errMsg: string) {
		this.snackBar.open(errMsg, undefined, {
			duration: 3000,
		});
		this.clearAll();
		this.goToOrganizationDetails();
	}
}
